import type { BoxProps, MantineSize } from "@mantine/core";
import type { UseQueryResult } from "@tanstack/react-query";
import type React from "react";

import { ErrorIndicator } from "./ErrorIndicator";
import { LoadingIndicator } from "./LoadingIndicator";

export interface QueryLoaderBoundaryProps<TData, TError = unknown> extends BoxProps {
  queryResult: UseQueryResult<TData, TError>;
  size?: MantineSize;
  children: ((data: TData) => React.JSX.Element | React.JSX.Element[] | null) | React.JSX.Element;
}

export function QueryLoaderBoundary<TData, TError = unknown>({
  queryResult,
  size = "md",
  children,
  ...boxProps
}: QueryLoaderBoundaryProps<TData, TError>): React.JSX.Element | React.JSX.Element[] | null {
  // Type narrowing via TanStack Query's discriminated union
  if (queryResult.isPending) {
    return <LoadingIndicator loaderSize={size} {...boxProps} />;
  }

  if (queryResult.isError) {
    return <ErrorIndicator error={queryResult.error} />;
  }

  // TypeScript guarantees: status === 'success', data is TData (not undefined)
  if (typeof children === "function") {
    return children(queryResult.data);
  }

  return children;
}
